import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Storefront, MapPin, MagnifyingGlass } from '@phosphor-icons/react';
import { useSettings } from '@/context/SettingsContext';

const PIN_KEY = 'zk_pin';

export default function Marketplace() {
  const { settings } = useSettings();
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || '');
  const [q, setQ] = useState('');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (pin && pin.length >= 4) params.pincode = pin.trim();
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get('/sellers/public', { params });
      setStores(data || []);
    } catch (e) {
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = (e) => {
    e.preventDefault();
    if (pin) localStorage.setItem(PIN_KEY, pin.trim());
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="marketplace-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-olive-700 font-semibold">Local Marketplace</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-stone-900 mt-2">Nearby Stores</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">
          Shop from vetted local stores in {settings?.service_area_label || 'your area'}. Pay cash on delivery, delivered straight to your door.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 bg-white border border-stone-200 rounded-2xl p-3 sm:p-4 mb-8">
        <div className="flex items-center gap-2 flex-1 bg-cream-100 rounded-xl px-3 h-11">
          <MapPin size={18} className="text-terracotta-600"/>
          <input
            data-testid="marketplace-pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="Your PIN code"
            inputMode="numeric"
            className="flex-1 bg-transparent focus:outline-none text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-1 bg-cream-100 rounded-xl px-3 h-11">
          <MagnifyingGlass size={18} className="text-stone-500"/>
          <input
            data-testid="marketplace-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search store name"
            className="flex-1 bg-transparent focus:outline-none text-sm"
          />
        </div>
        <button
          data-testid="marketplace-search-btn"
          type="submit"
          className="h-11 px-6 rounded-full bg-terracotta-600 text-white text-sm font-semibold hover:bg-terracotta-700"
        >
          Find stores
        </button>
      </form>

      {loading ? (
        <div className="py-16 text-center text-stone-500">Loading stores…</div>
      ) : stores.length === 0 ? (
        <div className="py-16 text-center bg-white border border-dashed border-stone-200 rounded-2xl">
          <Storefront size={40} className="mx-auto text-stone-400"/>
          <p className="mt-3 text-stone-700 font-semibold">No stores found{pin ? ` for PIN ${pin}` : ''}.</p>
          <p className="text-sm text-stone-500 mt-1">Try a different PIN code or clear the filters.</p>
          <Link to="/seller/register" className="inline-block mt-5 h-10 px-5 rounded-full bg-olive-700 text-white text-sm font-semibold hover:bg-olive-800 leading-10">Open your store here</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketplace-store-grid">
          {stores.map((s) => (
            <Link
              key={s.id}
              to={`/marketplace/store/${s.slug}`}
              data-testid={`store-card-${s.slug}`}
              className="group bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg hover:border-terracotta-300 transition"
            >
              <div className="h-28 bg-gradient-to-br from-cream-100 to-olive-100 relative">
                {s.banner_url ? (
                  <img src={s.banner_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-olive-600">
                    <Storefront size={40} weight="duotone"/>
                  </div>
                )}
                {!s.is_open && (
                  <span className="absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-900/80 text-white">CLOSED</span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg text-stone-900 group-hover:text-terracotta-700 truncate">{s.shop_name}</h3>
                </div>
                <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><MapPin size={12}/>PIN {s.pincode}</p>
                {s.description && <p className="text-sm text-stone-600 mt-2 line-clamp-2">{s.description}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
